import React, { useState } from 'react';
import { FormInstance } from 'antd';
import { Form } from '@common/components';
import { datetime, type DatetimeType } from '@common/services/datetime';
import { EditStaffingRequestFormPartTwo } from './_edit-staffing-request-form-part-two';
import { EditStaffingRequestFormPartOne } from './_edit-staffing-request-form-part-one';
import { EditStaffingRequestFormPartThree } from './_edit-staffing-request-form-part-three';
import {
  StaffingRequestType,
  ClinicalSpecialtyType,
  CertificationType,
  TechnologyType,
  CertificationCustomType,
  ClinicalSpecialtyCustomType,
} from '@jobs/graphql';

export type FormValuesType = {
  certifications?: string;
  description: string;
  externalId: string;
  hiringLeaderId: string;
  hoursPerShift: number;
  international: boolean;
  lengthInWeeks: number;
  openPositions: number;
  rate: number;
  shiftType: string;
  shiftsPerWeek: number;
  specialtyId?: string;
  startDate: DatetimeType;
  technologyIds: string[];
  yearsOfExperience: string;
  approvalChainTemplateSource?: string;
  commentContent?: {
    contentAsText?: string;
  };
};

type EditStaffingRequestFormProps = {
  staffingRequest: StaffingRequestType;
  clinicalSpecialties: ClinicalSpecialtyType[];
  technologies: TechnologyType[];
  certifications: CertificationType[];
  form: FormInstance;
  onSubmit: (
    values: FormValuesType,
    deviated: boolean,
    selectedSpecialties: ClinicalSpecialtyCustomType[],
    selectedCertifications: CertificationCustomType[]
  ) => void;
  partOneVisible: boolean;
  partTwoVisible: boolean;
  partThreeVisible: boolean;
  setIncreasedEstimatedCost?: (value: boolean) => void;
  onValuesChange: () => void;
  isApprovalNotesRequired?: boolean;
  enableOnlyOpenPositionsField?: boolean;
};

export function EditStaffingRequestForm({
  staffingRequest,
  clinicalSpecialties,
  technologies,
  certifications,
  form,
  onSubmit,
  partOneVisible,
  partTwoVisible,
  partThreeVisible,
  setIncreasedEstimatedCost = () => {},
  onValuesChange,
  isApprovalNotesRequired,
  enableOnlyOpenPositionsField,
}: EditStaffingRequestFormProps) {
  const hasBillingCard = !!staffingRequest.billingCard;
  const [isDefaultRate, setIsDefaultRate] = useState(
    staffingRequest.billingRegularRate ===
      staffingRequest.staffingRequestTemplate.billingRegularRate ||
      hasBillingCard
  );
  const [selectedSpecialties, setSelectedSpecialties] = useState<
    ClinicalSpecialtyCustomType[]
  >([]);
  const [selectedCertifications, setSelectedCertifications] = useState<
    CertificationCustomType[]
  >([]);
  const canUpdateDetails = staffingRequest.canUpdateDetails.value;
  const deviated = !isDefaultRate || !hasBillingCard;

  const initialValues = {
    startDate: datetime(staffingRequest.startDate),
    lengthInWeeks: staffingRequest.length,
    shiftsPerWeek: staffingRequest.shiftsPerWeek,
    hoursPerShift: staffingRequest.hoursPerShift,
    shiftType: staffingRequest.shiftType,
    rate: staffingRequest.billingRegularRate,
    openPositions: staffingRequest.openStaffingRequestPositionsCount,
    description: staffingRequest.description,
    hiringLeaderId: staffingRequest.hiringLeader?.id,
    externalId: staffingRequest.externalId,
    yearsOfExperience: staffingRequest.yearsOfExperience || undefined,
    technologyIds:
      staffingRequest.staffingRequestTechnologies?.map(
        (technology) => technology.technology.id
      ) || [],
    international: staffingRequest.international,
  };

  return (
    <div className="EditStaffingRequestForm">
      <Form
        layout="vertical"
        initialValues={initialValues}
        form={form}
        onValuesChange={onValuesChange}
        onFinish={(values: FormValuesType) =>
          onSubmit(
            values,
            deviated,
            selectedSpecialties,
            selectedCertifications
          )
        }
        validateMessages={{
          number: {
            range: 'Enter a number between ${min} and ${max}',
          },
        }}
      >
        <EditStaffingRequestFormPartOne
          form={form}
          deviated={deviated}
          visible={partOneVisible}
          staffingRequest={staffingRequest}
          setIncreasedEstimatedCost={setIncreasedEstimatedCost}
          setIsDefaultRate={setIsDefaultRate}
          isDefaultRate={isDefaultRate}
          canUpdateDetails={canUpdateDetails}
          enableOnlyOpenPositionsField={enableOnlyOpenPositionsField}
        />
        <EditStaffingRequestFormPartTwo
          staffingRequest={staffingRequest}
          clinicalSpecialties={clinicalSpecialties}
          technologies={technologies}
          certifications={certifications}
          visible={partTwoVisible}
          selectedSpecialties={selectedSpecialties}
          setSelectedSpecialties={setSelectedSpecialties}
          selectedCertifications={selectedCertifications}
          setSelectedCertifications={setSelectedCertifications}
        />
        <EditStaffingRequestFormPartThree
          disableApprovalChainSelection={enableOnlyOpenPositionsField}
          isApprovalNotesRequired={isApprovalNotesRequired}
          organizationUnitId={staffingRequest.organizationUnit.id}
          visible={partThreeVisible}
          form={form}
        />
      </Form>
      <style jsx>
        {`
          .EditStaffingRequestForm :global(.ant-input-number),
          .EditStaffingRequestForm :global(.ant-picker) {
            width: 100%;
          }
        `}
      </style>
    </div>
  );
}
